import React, { useState, useEffect, useCallback, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import { useI18n } from "./i18n";
import { trackPageView } from "./uxTracker";

/* ═══════════════════════════════════════════════════════════════════════════
   I18N — Translation table (fr, en, es, de, pt, ar)
═══════════════════════════════════════════════════════════════════════════ */
const WT = {
  // ── Header / intro
  "eyebrow":    { fr:"Exemple concret", en:"Concrete example", es:"Ejemplo concreto", de:"Konkretes Beispiel", pt:"Exemplo concreto", ar:"مثال ملموس" },
  "title.main": { fr:"Suivez un trade pas à pas",
                  en:"Follow a trade step by step",
                  es:"Sigue una operación paso a paso",
                  de:"Verfolgen Sie einen Trade Schritt für Schritt",
                  pt:"Acompanhe uma operação passo a passo",
                  ar:"تابع صفقة خطوة بخطوة" },
  "title.sub":  { fr:"Alex a 100 EVP et pense que l'IA va révolutionner la médecine.",
                  en:"Alex has 100 EVP and believes AI will revolutionize medicine.",
                  es:"Alex tiene 100 EVP y cree que la IA revolucionará la medicina.",
                  de:"Alex hat 100 EVP und glaubt, dass KI die Medizin revolutionieren wird.",
                  pt:"Alex tem 100 EVP e acredita que a IA vai revolucionar a medicina.",
                  ar:"لدى Alex 100 EVP ويعتقد أن الذكاء الاصطناعي سيُحدث ثورة في الطب." },
  "scenario":   { fr:"Alex trade sur un marché Tech",
                  en:"Alex trades on a Tech market",
                  es:"Alex opera en un mercado Tech",
                  de:"Alex tradet auf einem Tech-Markt",
                  pt:"Alex opera em um mercado Tech",
                  ar:"يتداول Alex في سوق التكنولوجيا" },

  // ── Step 1
  "s1.title":   { fr:"100 EVP offerts",
                  en:"100 EVP free",
                  es:"100 EVP gratis",
                  de:"100 EVP geschenkt",
                  pt:"100 EVP grátis",
                  ar:"100 EVP مجانًا" },
  "s1.body":    { fr:"Alex reçoit {b}100 EVP gratuits{/b} à l'inscription — aucun dépôt nécessaire.",
                  en:"Alex receives {b}100 free EVP{/b} on signup — no deposit needed.",
                  es:"Alex recibe {b}100 EVP gratis{/b} al registrarse — sin depósito.",
                  de:"Alex erhält {b}100 EVP kostenlos{/b} bei der Registrierung — keine Einzahlung nötig.",
                  pt:"Alex recebe {b}100 EVP grátis{/b} no cadastro — sem necessidade de depósito.",
                  ar:"يحصل Alex على {b}100 EVP مجانًا{/b} عند التسجيل — لا حاجة للإيداع." },
  "s1.hint":    { fr:"Bonus d'inscription · forfait fixe",
                  en:"Signup bonus · fixed amount",
                  es:"Bono de registro · monto fijo",
                  de:"Anmeldebonus · fester Betrag",
                  pt:"Bônus de cadastro · valor fixo",
                  ar:"مكافأة التسجيل · مبلغ ثابت" },

  // ── Step 2
  "s2.title":   { fr:"Il trouve un marché",
                  en:"He finds a market",
                  es:"Encuentra un mercado",
                  de:"Er findet einen Markt",
                  pt:"Ele encontra um mercado",
                  ar:"يجد سوقًا" },
  "s2.body":    { fr:"Il découvre la question : {b}« L'IA approuvée en médecine par la FDA avant 2026 ? »{/b} — outcome YES à 61%.",
                  en:"He discovers the question: {b}\"AI approved in medicine by the FDA before 2026?\"{/b} — YES outcome at 61%.",
                  es:"Descubre la pregunta: {b}«¿IA aprobada en medicina por la FDA antes de 2026?»{/b} — outcome YES a 61%.",
                  de:"Er entdeckt die Frage: {b}\"KI in der Medizin von der FDA vor 2026 zugelassen?\"{/b} — YES-Outcome bei 61%.",
                  pt:"Ele descobre a pergunta: {b}\"IA aprovada em medicina pela FDA antes de 2026?\"{/b} — outcome YES a 61%.",
                  ar:"يكتشف السؤال: {b}\"هل سيوافق FDA على الذكاء الاصطناعي في الطب قبل 2026؟\"{/b} — نتيجة YES بسعر 61 سنتًا." },
  "market.q":   { fr:"L'IA approuvée en médecine par la FDA avant 2026 ?",
                  en:"AI approved in medicine by the FDA before 2026?",
                  es:"¿IA aprobada en medicina por la FDA antes de 2026?",
                  de:"KI in der Medizin von der FDA vor 2026 zugelassen?",
                  pt:"IA aprovada em medicina pela FDA antes de 2026?",
                  ar:"هل سيوافق FDA على الذكاء الاصطناعي في الطب قبل 2026؟" },

  // ── Step 3
  "s3.title":   { fr:"Il achète des shares",
                  en:"He buys shares",
                  es:"Compra acciones",
                  de:"Er kauft Anteile",
                  pt:"Ele compra shares",
                  ar:"يشتري الأسهم" },
  "s3.body":    { fr:"Convaincu, il achète {b}163 shares YES à 61%{/b}, soit {b}99,43 EVP investis{/b}. Reste dans la limite des 5 trades par événement.",
                  en:"Convinced, he buys {b}163 YES shares at 61%{/b} = {b}99.43 EVP invested{/b}. Within the 5 trades per market limit.",
                  es:"Convencido, compra {b}163 shares YES a 61%{/b} = {b}99,43 EVP invertidos{/b}. Dentro del límite de 5 operaciones por mercado.",
                  de:"Überzeugt kauft er {b}163 YES-Anteile zu 61%{/b} = {b}99,43 EVP investiert{/b}. Innerhalb der 5-Trades-pro-Markt-Grenze.",
                  pt:"Convencido, compra {b}163 shares YES a 61%{/b} = {b}99,43 EVP investidos{/b}. Dentro do limite de 5 operações por mercado.",
                  ar:"مقتنعًا، يشتري {b}163 سهم YES بسعر 61 سنتًا{/b} = {b}99.43 EVP مستثمرة{/b}. ضمن حد 5 صفقات لكل سوق." },
  "s3.hint":    { fr:"Limite de trades respectée",
                  en:"Trade limit respected",
                  es:"Límite de operaciones respetado",
                  de:"Trade-Limit eingehalten",
                  pt:"Limite de operações respeitado",
                  ar:"تم احترام حد الصفقات" },

  // ── Step 4
  "s4.title":   { fr:"La probabilité monte",
                  en:"The probability rises",
                  es:"La probabilidad sube",
                  de:"Die Wahrscheinlichkeit steigt",
                  pt:"A probabilidade sobe",
                  ar:"ترتفع الاحتمالية" },
  "s4.body":    { fr:"Deux mois plus tard, la probabilité passe à {b}80%{/b}. Ses 163 shares valent maintenant {b}130,4 EVP{/b}.",
                  en:"Two months later, the probability climbs to {b}80%{/b}. His 163 shares are now worth {b}130.4 EVP{/b}.",
                  es:"Dos meses después, la probabilidad sube a {b}80%{/b}. Sus 163 shares ahora valen {b}130,4 EVP{/b}.",
                  de:"Zwei Monate später steigt die Wahrscheinlichkeit auf {b}80%{/b}. Seine 163 Anteile sind jetzt {b}130,4 EVP{/b} wert.",
                  pt:"Dois meses depois, a probabilidade sobe para {b}80%{/b}. Seus 163 shares agora valem {b}130,4 EVP{/b}.",
                  ar:"بعد شهرين، ترتفع الاحتمالية إلى {b}80 سنتًا{/b}. أسهمه الـ163 تبلغ قيمتها الآن {b}130.4 EVP{/b}." },

  // ── Step 5
  "s5.title":   { fr:"Il revend pour le profit",
                  en:"He sells for profit",
                  es:"Vende con beneficio",
                  de:"Er verkauft mit Gewinn",
                  pt:"Ele vende com lucro",
                  ar:"يبيع لتحقيق الربح" },
  "s5.body":    { fr:"Il revend ses shares et empoche {b}+30,97 EVP de profit{/b}. Son solde atteint {b}130,97 EVP{/b} — au-dessus de la base de 100 !",
                  en:"He sells his shares and pockets {b}+30.97 EVP profit{/b}. His balance reaches {b}130.97 EVP{/b} — above the 100 base!",
                  es:"Vende sus shares y se embolsa {b}+30,97 EVP de beneficio{/b}. Su saldo llega a {b}130,97 EVP{/b} — ¡por encima de la base de 100!",
                  de:"Er verkauft seine Anteile und steckt {b}+30,97 EVP Gewinn{/b} ein. Sein Guthaben erreicht {b}130,97 EVP{/b} — über der 100er-Basis!",
                  pt:"Ele vende seus shares e embolsa {b}+30,97 EVP de lucro{/b}. Seu saldo chega a {b}130,97 EVP{/b} — acima da base de 100!",
                  ar:"يبيع أسهمه ويجني {b}+30.97 EVP من الربح{/b}. يصل رصيده إلى {b}130.97 EVP{/b} — فوق قاعدة الـ100!" },
  "s5.tip":     { fr:"💡 30 EVP gagnés < seuil de 100 EVP au-dessus de la base. Quand Alex atteindra 200 EVP, il pourra retirer 1 $ — s'il a aussi ≥10 trades sur ≥3 jours.",
                  en:"💡 30 EVP earned < the 100 EVP threshold above base. When Alex reaches 200 EVP, he can withdraw $1 — if he also has ≥10 trades over ≥3 days.",
                  es:"💡 30 EVP ganados < umbral de 100 EVP sobre la base. Cuando Alex alcance 200 EVP, podrá retirar 1 $ — si también tiene ≥10 operaciones en ≥3 días.",
                  de:"💡 30 EVP verdient < 100-EVP-Schwelle über der Basis. Wenn Alex 200 EVP erreicht, kann er 1 $ abheben — bei ≥10 Trades über ≥3 Tage.",
                  pt:"💡 30 EVP ganhos < limite de 100 EVP acima da base. Quando Alex atingir 200 EVP, poderá sacar 1 $ — se também tiver ≥10 operações em ≥3 dias.",
                  ar:"💡 30 EVP مكتسبة < عتبة 100 EVP فوق القاعدة. عندما يصل Alex إلى 200 EVP، يمكنه سحب 1 دولار — إذا كان لديه أيضًا ≥10 صفقات خلال ≥3 أيام." },

  // ── Step 6 (CTA)
  "s6.title":   { fr:"À vous de jouer !",
                  en:"Your turn to play!",
                  es:"¡Tu turno!",
                  de:"Sie sind dran!",
                  pt:"Sua vez de jogar!",
                  ar:"دورك للعب!" },
  "s6.body":    { fr:"Comme Alex, recevez {b}100 EVP gratuits{/b} à l'inscription et commencez à trader vos convictions sur des événements réels.",
                  en:"Like Alex, get {b}100 free EVP{/b} on signup and start trading your convictions on real-world events.",
                  es:"Como Alex, recibe {b}100 EVP gratis{/b} al registrarte y empieza a operar tus convicciones en eventos reales.",
                  de:"Wie Alex erhalten Sie {b}100 EVP gratis{/b} bei der Registrierung und beginnen Sie, Ihre Überzeugungen zu echten Ereignissen zu traden.",
                  pt:"Como Alex, receba {b}100 EVP grátis{/b} no cadastro e comece a operar suas convicções em eventos reais.",
                  ar:"مثل Alex، احصل على {b}100 EVP مجانًا{/b} عند التسجيل وابدأ في تداول قناعاتك على أحداث حقيقية." },
  "s6.title_in":{ fr:"Prêt à trader comme Alex ?",
                  en:"Ready to trade like Alex?",
                  es:"¿Listo para operar como Alex?",
                  de:"Bereit, wie Alex zu traden?",
                  pt:"Pronto para operar como Alex?",
                  ar:"هل أنت مستعد للتداول مثل Alex؟" },
  "s6.body_in": { fr:"Explorez les marchés ouverts et placez votre premier trade dès maintenant.",
                  en:"Explore open markets and place your first trade now.",
                  es:"Explora los mercados abiertos y realiza tu primera operación ahora.",
                  de:"Entdecken Sie offene Märkte und platzieren Sie jetzt Ihren ersten Trade.",
                  pt:"Explore mercados abertos e faça sua primeira operação agora.",
                  ar:"استكشف الأسواق المفتوحة وأجرِ صفقتك الأولى الآن." },

  // ── Buttons
  "btn.signup": { fr:"Créer un compte gratuit", en:"Create a free account", es:"Crear cuenta gratis", de:"Kostenlos registrieren", pt:"Criar conta grátis", ar:"إنشاء حساب مجاني" },
  "btn.login":  { fr:"J'ai déjà un compte",      en:"I already have an account", es:"Ya tengo una cuenta", de:"Ich habe bereits ein Konto", pt:"Já tenho uma conta", ar:"لدي حساب بالفعل" },
  "btn.go":     { fr:"Voir les marchés",          en:"Browse markets",         es:"Ver mercados",        de:"Märkte erkunden",     pt:"Ver mercados",        ar:"تصفّح الأسواق" },
  "btn.prev":   { fr:"Précédent",                 en:"Previous",               es:"Anterior",            de:"Zurück",              pt:"Anterior",            ar:"السابق" },
  "btn.next":   { fr:"Suivant",                   en:"Next",                   es:"Siguiente",           de:"Weiter",              pt:"Próximo",             ar:"التالي" },
  "btn.start":  { fr:"Commencer",                 en:"Start",                  es:"Comenzar",            de:"Loslegen",            pt:"Começar",             ar:"ابدأ" },

  // ── Misc
  "step_of":    { fr:"Étape {n} sur {total}",     en:"Step {n} of {total}",    es:"Paso {n} de {total}", de:"Schritt {n} von {total}", pt:"Etapa {n} de {total}", ar:"الخطوة {n} من {total}" },
  "fab.hide":   { fr:"Masquer",                    en:"Hide",                   es:"Ocultar",             de:"Ausblenden",          pt:"Ocultar",             ar:"إخفاء" },
  "fab.label":  { fr:"Voir un exemple",           en:"See an example",         es:"Ver un ejemplo",      de:"Beispiel ansehen",    pt:"Ver um exemplo",      ar:"شاهد مثالاً" },
  "calc.invest":{ fr:"Investi",                   en:"Invested",               es:"Invertido",           de:"Investiert",          pt:"Investido",           ar:"المستثمر" },
  "calc.start": { fr:"Solde initial",             en:"Starting balance",       es:"Saldo inicial",       de:"Anfangsguthaben",     pt:"Saldo inicial",       ar:"الرصيد الأولي" },
  "calc.final": { fr:"Solde final",               en:"Final balance",          es:"Saldo final",         de:"Endsaldo",            pt:"Saldo final",         ar:"الرصيد النهائي" },
  "calc.profit":{ fr:"Profit net",                en:"Net profit",             es:"Beneficio neto",      de:"Nettogewinn",         pt:"Lucro líquido",       ar:"صافي الربح" },
  "yes":        { fr:"OUI",                       en:"YES",                    es:"SÍ",                  de:"JA",                  pt:"SIM",                 ar:"نعم" },
  "no":         { fr:"NON",                       en:"NO",                     es:"NO",                  de:"NEIN",                pt:"NÃO",                 ar:"لا" },
};

function wt(key, lang, vars = {}) {
  const v = WT[key]?.[lang] ?? WT[key]?.fr ?? key;
  return Object.entries(vars).reduce((acc, [k, val]) => acc.replaceAll(`{${k}}`, val), v);
}

/* Parse {b}…{/b} into JSX (highlighted bold + colored) */
function parseBold(text, highlightColor = "#00e676") {
  const parts = String(text).split(/(\{b\}|\{\/b\})/g);
  let bold = false;
  return parts.filter(p => p !== "{b}" && p !== "{/b}").map((part, i) => {
    const isBoldPart = parts.slice(0, parts.indexOf(part, 0) + 1).filter(x => x === "{b}").length >
                        parts.slice(0, parts.indexOf(part, 0) + 1).filter(x => x === "{/b}").length;
    return part;
  });
}

/* Simpler robust parser */
function renderBoldText(text, color = "#00e676") {
  const parts = [];
  let remaining = String(text);
  let key = 0;
  while (remaining.length) {
    const start = remaining.indexOf("{b}");
    if (start === -1) { parts.push(remaining); break; }
    if (start > 0) parts.push(remaining.slice(0, start));
    remaining = remaining.slice(start + 3);
    const end = remaining.indexOf("{/b}");
    if (end === -1) { parts.push(remaining); break; }
    parts.push(<strong key={++key} style={{ color, fontWeight: 700 }}>{remaining.slice(0, end)}</strong>);
    remaining = remaining.slice(end + 4);
  }
  return parts;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Animations
═══════════════════════════════════════════════════════════════════════════ */
const fadeIn   = keyframes`from{opacity:0}to{opacity:1}`;
const slideUp  = keyframes`from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}`;
const pulseDot = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(255,193,7,.5); }
  70%  { box-shadow: 0 0 0 10px rgba(255,193,7,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,193,7,0); }
`;
const sparkle  = keyframes`
  0%,100% { transform: scale(1) rotate(0); opacity:1 }
  50%     { transform: scale(1.2) rotate(15deg); opacity:.7 }
`;
const growBar = keyframes`from{width:0}to{width:var(--w,100%)}`;
const countUp = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const bobUp   = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}`;

/* ═══════════════════════════════════════════════════════════════════════════
   FAB (mobile only, fixed bottom)
═══════════════════════════════════════════════════════════════════════════ */
const FabWrap = styled.div`
  position: fixed;
  bottom: 4.25rem;
  right: 1.25rem;
  z-index: 195;
  @media (min-width: 769px) {
    bottom: 1.75rem;
    right: 1.75rem;
  }
`;
const FabClose = styled.button`
  position: absolute; top: -9px; right: -5px; z-index: 1;
  width: 22px; height: 22px; padding: 0;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
  border: 1px solid ${({theme}) => theme.borderLight};
  background: ${({theme}) => theme.bgCard};
  color: ${({theme}) => theme.textMuted};
  font-size: .62rem; line-height: 1; cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,.45);
  transition: color .15s, border-color .15s;
  &:hover { color: ${({theme}) => theme.text}; border-color: ${({theme}) => theme.green}88; }
`;
const Fab = styled.button`
  display: flex;
  align-items: center;
  gap: .5rem;
  padding: .8rem 1rem;
  border-radius: 100px;
  border: 1px solid ${({theme}) => theme.green}88;
  background: linear-gradient(135deg, ${({theme}) => theme.bgCard}, ${({theme}) => theme.bgAide});
  color: ${({theme}) => theme.green};
  font-family: ${({theme}) => theme.fontBody};
  font-size: .78rem;
  font-weight: 600;
  letter-spacing: .02em;
  cursor: pointer;
  box-shadow: 0 6px 20px rgba(0,0,0,.5), 0 0 24px rgba(255,193,7,.15);
  animation: ${pulseDot} 2.4s ease infinite, ${bobUp} 3s ease infinite;
  transition: transform .15s, box-shadow .2s;
  &:hover, &:active {
    transform: scale(1.04);
    box-shadow: 0 8px 28px rgba(0,0,0,.6), 0 0 32px rgba(255,193,7,.3);
  }
  /* Desktop : un peu plus d'espace + texte légèrement plus grand */
  @media (min-width: 769px) {
    padding: .9rem 1.3rem;
    font-size: .82rem;
  }
`;
const FabIcon = styled.span`font-size: 1.05rem; animation: ${sparkle} 2s ease infinite;`;

/* ═══════════════════════════════════════════════════════════════════════════
   Modal shell
═══════════════════════════════════════════════════════════════════════════ */
const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 400;
  background: rgba(0,0,0,.88);
  backdrop-filter: blur(10px);
  display: flex; align-items: center; justify-content: center;
  padding: 1rem;
  animation: ${fadeIn} .2s ease;
`;
const Modal = styled.div`
  width: 100%; max-width: 540px;
  max-height: 92vh; overflow: hidden;
  background: ${({theme}) => theme.bgCard};
  border: 1px solid ${({theme}) => theme.borderLight};
  border-radius: 18px;
  display: flex; flex-direction: column;
  animation: ${slideUp} .3s cubic-bezier(.4,0,.2,1);
  background-image:
    radial-gradient(ellipse 80% 60% at 30% 0%, rgba(0,230,118,.06), transparent 60%),
    radial-gradient(ellipse 60% 40% at 80% 100%, rgba(255,193,7,.05), transparent 60%);
`;

const Head = styled.div`
  padding: 1rem 1.25rem .75rem;
  border-bottom: 1px solid ${({theme}) => theme.border};
  display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem;
  background: transparent;
`;
const HeadText = styled.div`flex:1;min-width:0;`;
const Eyebrow = styled.div`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .58rem; letter-spacing: .12em;
  color: ${({theme}) => theme.gold};
  text-transform: uppercase; margin-bottom: .25rem;
  display: flex; align-items: center; gap: .35rem;
`;
const Title = styled.h2`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: 1.1rem; font-weight: 700; line-height: 1.2;
  letter-spacing: -.02em; color: ${({theme}) => theme.text};
`;
const CloseBtn = styled.button`
  background: none; border: none; color: ${({theme}) => theme.textMuted};
  font-size: 1.1rem; cursor: pointer; padding: .25rem;
  &:hover { color: ${({theme}) => theme.text}; }
`;

/* Progress (dots + step counter) */
const Progress = styled.div`
  padding: .65rem 1.25rem;
  display: flex; justify-content: space-between; align-items: center;
  border-bottom: 1px solid ${({theme}) => theme.border};
`;
const Dots = styled.div`display:flex; gap:.35rem;`;
const Dot = styled.button`
  border: none; padding: 0; cursor: pointer; background: transparent;
  width: ${({$active}) => $active ? "24px" : "8px"};
  height: 6px; border-radius: 100px;
  background: ${({$active, $done, theme}) =>
    $active ? theme.gold :
    $done   ? theme.green :
              theme.border};
  transition: all .3s ease;
  &:hover { background: ${({$active, theme}) => $active ? theme.gold : theme.borderLight}; }
`;
const Counter = styled.span`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .62rem; color: ${({theme}) => theme.textMuted};
  letter-spacing: .04em;
`;

/* Carousel */
const Carousel = styled.div`
  overflow: hidden;
  flex: 1; min-height: 360px;
  position: relative;
`;
const Track = styled.div`
  display: flex;
  width: 100%; height: 100%;
  transform: translateX(${({$idx, $isRtl}) => $isRtl ? `${$idx * 100}%` : `-${$idx * 100}%`});
  transition: transform .35s cubic-bezier(.4,0,.2,1);
`;
const Step = styled.div`
  flex: 0 0 100%; min-width: 100%;
  padding: 1.5rem 1.5rem 1rem;
  overflow-y: auto;
  display: flex; flex-direction: column; gap: 1rem;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;
const StepHeader = styled.div`
  display: flex; align-items: flex-start; gap: .85rem;
  animation: ${countUp} .35s ease both;
`;
const StepNumber = styled.div`
  width: 36px; height: 36px; flex-shrink: 0;
  border-radius: 50%;
  background: ${({$c, theme}) =>
    $c === "green"  ? `linear-gradient(135deg, ${theme.green}, #00b85e)` :
    $c === "gold"   ? `linear-gradient(135deg, ${theme.gold}, #f0a500)` :
    $c === "accent" ? `linear-gradient(135deg, ${theme.accent}, #2196F3)` :
                      theme.bgHover};
  color: ${({$c}) => $c === "gold" ? "#000" : "#fff"};
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: 1rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 8px rgba(0,0,0,.4);
`;
const StepTitle = styled.h3`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: 1.05rem; font-weight: 700;
  letter-spacing: -.01em; line-height: 1.25;
  margin-top: .35rem;
`;
const StepBody = styled.div`
  font-size: .82rem; line-height: 1.55;
  color: ${({theme}) => theme.textDim};
  animation: ${countUp} .35s ease both .05s;
`;

/* Visuals — generic */
const Visual = styled.div`
  background: ${({theme}) => theme.bg};
  border: 1px solid ${({theme}) => theme.border};
  border-radius: 12px;
  padding: 1.1rem 1.2rem;
  animation: ${countUp} .35s ease both .1s;
`;

/* Hint pill */
const Hint = styled.div`
  display: inline-flex; align-items: center; gap: .4rem;
  font-family: ${({theme}) => theme.fontMono};
  font-size: .65rem; letter-spacing: .03em;
  color: ${({theme}) => theme.green};
  background: ${({theme}) => theme.greenDim};
  border: 1px solid ${({theme}) => theme.green}33;
  padding: .25rem .65rem; border-radius: 100px;
`;

/* Tip block */
const TipBlock = styled.div`
  display: flex; gap: .55rem; align-items: flex-start;
  padding: .7rem .85rem;
  background: rgba(0,230,118,.06);
  border: 1px solid ${({theme}) => theme.green}33;
  border-radius: 8px;
  font-size: .72rem; line-height: 1.5;
  color: ${({theme}) => theme.textDim};
  margin-top: auto;
`;

/* Nav footer */
const Nav = styled.div`
  padding: .85rem 1.25rem;
  border-top: 1px solid ${({theme}) => theme.border};
  display: grid; grid-template-columns: 1fr 1fr; gap: .6rem;
  background: ${({theme}) => theme.bgCard};
`;
const NavBtn = styled.button`
  padding: .65rem 1rem;
  border-radius: 8px;
  font-family: ${({theme}) => theme.fontBody};
  font-size: .8rem; font-weight: 600; letter-spacing: .02em;
  cursor: ${({disabled}) => disabled ? "not-allowed" : "pointer"};
  transition: all .15s;
  border: 1px solid ${({$primary, theme}) =>
    $primary ? "transparent" : theme.border};
  background: ${({$primary, theme, disabled}) =>
    disabled  ? "transparent" :
    $primary  ? theme.gold :
                "transparent"};
  color: ${({$primary, theme, disabled}) =>
    disabled  ? theme.textMuted :
    $primary  ? "#000" :
                theme.textDim};
  &:hover:not(:disabled) {
    background: ${({$primary, theme}) =>
      $primary ? theme.gold : theme.bgHover};
    color: ${({$primary, theme}) => $primary ? "#000" : theme.text};
    opacity: ${({$primary}) => $primary ? .9 : 1};
  }
`;

/* ─── Visuals per step ─────────────────────────────────────────────────── */

/* Step 1 — 100 EVP free badge */
const BadgeWrap = styled.div`
  display: flex; align-items: center; justify-content: center;
  padding: 1.5rem 1rem;
  position: relative;
`;
const BigBadge = styled.div`
  position: relative;
  display: inline-flex; flex-direction: column; align-items: center; gap: .25rem;
  padding: 1.2rem 2rem;
  background: radial-gradient(ellipse at center, rgba(0,230,118,.15), rgba(0,230,118,.04));
  border: 1px solid ${({theme}) => theme.green}66;
  border-radius: 18px;
  box-shadow: 0 0 40px rgba(0,230,118,.15);
`;
const BigNum = styled.div`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: 2.6rem; font-weight: 700; line-height: 1;
  color: ${({theme}) => theme.green};
  letter-spacing: -.04em;
`;
const SmallLbl = styled.div`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .65rem; letter-spacing: .08em;
  color: ${({theme}) => theme.green};
  text-transform: uppercase;
`;
const Sparkle = styled.span`
  position: absolute; font-size: 1rem;
  animation: ${sparkle} 2s ease infinite;
  animation-delay: ${({$d}) => $d || 0}s;
  top: ${({$top}) => $top || "auto"};
  left: ${({$left}) => $left || "auto"};
  right: ${({$right}) => $right || "auto"};
  bottom: ${({$bottom}) => $bottom || "auto"};
`;

/* Step 2 — Market card preview */
const MarketCard = styled.div`
  display: flex; flex-direction: column; gap: .65rem;
`;
const MarketQ = styled.div`
  font-family: ${({theme}) => theme.fontBody};
  font-size: .9rem; font-weight: 600;
  line-height: 1.35; color: ${({theme}) => theme.text};
`;
const Outcomes = styled.div`display: flex; gap: .5rem;`;
const OutcomePill = styled.div`
  flex: 1; padding: .55rem .75rem;
  border-radius: 8px;
  border: 1px solid ${({$active, theme}) => $active ? theme.green : theme.border};
  background: ${({$active, theme}) => $active ? "rgba(0,230,118,.08)" : theme.bgHover};
  display: flex; justify-content: space-between; align-items: center;
`;
const OutcomeName = styled.span`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .7rem; font-weight: 700; letter-spacing: .04em;
  color: ${({$active, theme}) => $active ? theme.green : theme.textDim};
`;
const OutcomePct = styled.span`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .85rem; font-weight: 700;
  color: ${({$active, theme}) => $active ? theme.green : theme.text};
`;

/* Step 3 — Calculation */
const CalcRow = styled.div`
  display: flex; justify-content: space-between; align-items: baseline;
  padding: .4rem 0;
  font-family: ${({theme}) => theme.fontMono};
  font-size: .78rem;
`;
const CalcKey = styled.span`color: ${({theme}) => theme.textMuted};`;
const CalcVal = styled.span`
  color: ${({$accent, theme}) => $accent ? theme.green : theme.text};
  font-weight: ${({$accent}) => $accent ? 700 : 400};
`;
const CalcDivider = styled.div`
  height: 1px; background: ${({theme}) => theme.border}; margin: .3rem 0;
`;
const Bar = styled.div`
  height: 6px; border-radius: 100px; background: ${({theme}) => theme.bg};
  overflow: hidden; margin-top: .4rem;
`;
const BarFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, ${({theme}) => theme.green}, ${({theme}) => theme.accent});
  border-radius: 100px;
  --w: ${({$pct}) => $pct}%;
  animation: ${growBar} .8s cubic-bezier(.4,0,.2,1) both .15s;
`;

/* Step 4 — Probability rise */
const PriceRow = styled.div`
  display: flex; align-items: center; justify-content: center; gap: .7rem;
  padding: .4rem 0;
`;
const PriceTag = styled.div`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: 1.6rem; font-weight: 700;
  color: ${({$c, theme}) => $c === "gold" ? theme.gold : theme.green};
  letter-spacing: -.02em;
`;
const Arrow = styled.div`
  font-size: 1.5rem;
  color: ${({theme}) => theme.accent};
  animation: ${bobUp} 1.5s ease infinite;
`;
const PriceLbl = styled.div`
  font-family: ${({theme}) => theme.fontMono};
  font-size: .58rem; letter-spacing: .08em;
  color: ${({theme}) => theme.textMuted};
  text-align: center;
  text-transform: uppercase;
  margin-top: .15rem;
`;

/* Step 5 — Final balance */
const BalanceCalc = styled.div`
  display: flex; flex-direction: column; gap: .3rem;
`;
const ProfitNum = styled.div`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: 1.8rem; font-weight: 700;
  color: ${({theme}) => theme.green};
  letter-spacing: -.02em;
  text-align: center;
  text-shadow: 0 0 20px rgba(0,230,118,.3);
`;

/* Step 6 — CTA */
const CtaWrap = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 1rem;
  padding: 1rem .5rem;
`;
const CtaIcon = styled.div`
  width: 64px; height: 64px; border-radius: 50%;
  background: radial-gradient(ellipse at center, rgba(255,193,7,.2), rgba(255,193,7,.04));
  border: 2px solid ${({theme}) => theme.gold};
  display: flex; align-items: center; justify-content: center;
  font-size: 1.8rem;
  animation: ${sparkle} 2.4s ease infinite;
`;
const CtaTitle = styled.h3`
  font-family: ${({theme}) => theme.fontDisplay};
  font-size: 1.3rem; font-weight: 700;
  letter-spacing: -.02em; text-align: center;
  color: ${({theme}) => theme.text};
`;
const CtaBody = styled.p`
  font-size: .82rem; line-height: 1.55;
  color: ${({theme}) => theme.textDim};
  text-align: center; max-width: 320px;
`;
const CtaButtons = styled.div`
  display: flex; flex-direction: column; gap: .55rem;
  width: 100%; max-width: 320px; margin-top: .5rem;
`;
const PrimaryBtn = styled.button`
  padding: .85rem 1rem;
  border-radius: 10px; border: none;
  background: ${({theme}) => theme.gold};
  color: #000;
  font-family: ${({theme}) => theme.fontBody};
  font-size: .88rem; font-weight: 700;
  cursor: pointer; transition: opacity .15s;
  &:hover { opacity: .9; }
`;
const SecondaryBtn = styled.button`
  padding: .75rem 1rem;
  border-radius: 10px;
  border: 1px solid ${({theme}) => theme.borderLight};
  background: transparent;
  color: ${({theme}) => theme.textDim};
  font-family: ${({theme}) => theme.fontBody};
  font-size: .82rem; font-weight: 600;
  cursor: pointer; transition: all .15s;
  &:hover { background: ${({theme}) => theme.bgHover}; color: ${({theme}) => theme.text}; }
`;

/* ═══════════════════════════════════════════════════════════════════════════
   STEP CONTENT (visuals)
═══════════════════════════════════════════════════════════════════════════ */

function StepOneVisual() {
  return (
    <BadgeWrap>
      <Sparkle $top="0" $left="10%" $d={0}>✨</Sparkle>
      <Sparkle $top="20%" $right="8%" $d={0.5}>⭐</Sparkle>
      <Sparkle $bottom="10%" $left="15%" $d={1}>💫</Sparkle>
      <BigBadge>
        <BigNum>100</BigNum>
        <SmallLbl>EVP gratuits</SmallLbl>
      </BigBadge>
    </BadgeWrap>
  );
}

function StepTwoVisual({ t }) {
  return (
    <MarketCard>
      <MarketQ>{t("market.q")}</MarketQ>
      <Outcomes>
        <OutcomePill $active>
          <OutcomeName $active>✓ {t("yes")}</OutcomeName>
          <OutcomePct $active>61%</OutcomePct>
        </OutcomePill>
        <OutcomePill>
          <OutcomeName>{t("no")}</OutcomeName>
          <OutcomePct>39%</OutcomePct>
        </OutcomePill>
      </Outcomes>
    </MarketCard>
  );
}

function StepThreeVisual({ t }) {
  return (
    <>
      <CalcRow>
        <CalcKey>163 shares × 0,61%</CalcKey>
        <CalcVal>99,43 EVP</CalcVal>
      </CalcRow>
      <CalcDivider />
      <CalcRow>
        <CalcKey>{t("calc.invest")}</CalcKey>
        <CalcVal $accent>99,43 EVP</CalcVal>
      </CalcRow>
      <Bar><BarFill $pct={99.43} /></Bar>
      <div style={{display:"flex",justifyContent:"space-between",fontSize:".62rem",
        fontFamily:"DM Mono,monospace",color:"#5a6a7a",marginTop:".25rem"}}>
        <span>0 EVP</span><span>100 EVP</span>
      </div>
    </>
  );
}

function StepFourVisual() {
  return (
    <PriceRow>
      <div>
        <PriceTag>61%</PriceTag>
        <PriceLbl>Avant</PriceLbl>
      </div>
      <Arrow>→</Arrow>
      <div>
        <PriceTag $c="gold">80%</PriceTag>
        <PriceLbl>Après</PriceLbl>
      </div>
    </PriceRow>
  );
}

function StepFiveVisual({ t }) {
  return (
    <BalanceCalc>
      <CalcRow>
        <CalcKey>{t("calc.start")}</CalcKey>
        <CalcVal>100,00 EVP</CalcVal>
      </CalcRow>
      <CalcRow>
        <CalcKey>− Investi</CalcKey>
        <CalcVal>−99,43 EVP</CalcVal>
      </CalcRow>
      <CalcRow>
        <CalcKey>+ Revente</CalcKey>
        <CalcVal>+130,40 EVP</CalcVal>
      </CalcRow>
      <CalcDivider />
      <CalcRow>
        <CalcKey>{t("calc.final")}</CalcKey>
        <CalcVal $accent>130,97 EVP</CalcVal>
      </CalcRow>
      <ProfitNum>+30,97 EVP</ProfitNum>
      <PriceLbl style={{textAlign:"center"}}>{t("calc.profit")}</PriceLbl>
    </BalanceCalc>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN WIDGET — FAB + Modal
═══════════════════════════════════════════════════════════════════════════ */

const FAB_HIDDEN_KEY = "evenmt_example_fab_hidden";
const OPEN_EVENT     = "openTradingExample";

export default function TradingExampleWidget({
  user,
  onLoginClick,
  onSignupClick,
  onNavigateToMarkets,
  showFab = true,   // false : pas de bouton flottant (ex. page "Comment ça marche")
  defaultOpen = false, // true : s'ouvre dès le montage (instance locale de "Comment ça marche")
  onClose,             // appelé à la fermeture
}) {
  const { lang } = useI18n();
  const t = (key, vars) => wt(key, lang, vars);
  const isRtl = lang === "ar";

  const [open, setOpen] = useState(defaultOpen);
  const [idx,  setIdx]  = useState(0);

  /* Bouton flottant masquable — choix mémorisé dans le navigateur */
  const [fabHidden, setFabHidden] = useState(() => {
    try { return localStorage.getItem(FAB_HIDDEN_KEY) === "1"; } catch { return false; }
  });
  const hideFab = (e) => {
    e.stopPropagation();
    setFabHidden(true);
    try { localStorage.setItem(FAB_HIDDEN_KEY, "1"); } catch {}
  };

  /* Ouverture à distance (ex. depuis "Comment ça marche"), même bouton masqué :
     window.dispatchEvent(new CustomEvent("openTradingExample")) */
  useEffect(() => {
    const onOpen = (e) => {
      e.preventDefault?.();   // signale à l'émetteur que l'événement a été pris en charge
      setIdx(0); setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  const TOTAL = 6;
  const isLast = idx === TOTAL - 1;

  /* Reset au reopen */
  const handleOpen  = () => { setIdx(0); setOpen(true); };
  const handleClose = () => { setOpen(false); onClose?.(); };

  useEffect(() => {open && trackPageView("Exemple"); }, [open]);
  
  /* Lock body scroll */
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  /* Keyboard nav */
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") handleClose();
      if (e.key === "ArrowRight") setIdx(i => Math.min(i + 1, TOTAL - 1));
      if (e.key === "ArrowLeft")  setIdx(i => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  /* Touch swipe */
  const touchStartX = useRef(null);
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if ((diff > 0 && !isRtl) || (diff < 0 && isRtl))
        setIdx(i => Math.min(i + 1, TOTAL - 1));
      else
        setIdx(i => Math.max(i - 1, 0));
    }
    touchStartX.current = null;
  };

  const next = () => setIdx(i => Math.min(i + 1, TOTAL - 1));
  const prev = () => setIdx(i => Math.max(i - 1, 0));

  const handleSignup = () => { handleClose(); onSignupClick?.(); };
  const handleLogin  = () => { handleClose(); onLoginClick?.(); };
  const handleGo     = () => { handleClose(); onNavigateToMarkets?.(); };

  return (
    <>
      {/* FAB — masquable (✕), l'exemple reste accessible via "Comment ça marche" */}
      {showFab && !fabHidden && (
        <FabWrap>
          <FabClose type="button" onClick={hideFab} aria-label={t("fab.hide")} title={t("fab.hide")}>✕</FabClose>
          <Fab onClick={handleOpen} aria-label={t("fab.label")}>
            <FabIcon>💡</FabIcon>
            {t("fab.label")}
          </Fab>
        </FabWrap>
      )}

      {/* Modal */}
      {open && (
        <Overlay onClick={e => e.target === e.currentTarget && handleClose()}
          dir={isRtl ? "rtl" : "ltr"}>
          <Modal>
            <Head>
              <HeadText>
                <Eyebrow>📁 {t("eyebrow")} — {t("scenario")}</Eyebrow>
                <Title>{t("title.main")}</Title>
              </HeadText>
              <CloseBtn onClick={handleClose} aria-label="Close">✕</CloseBtn>
            </Head>

            <Progress>
              <Dots>
                {Array.from({length: TOTAL}).map((_, i) => (
                  <Dot
                    key={i}
                    $active={i === idx}
                    $done={i < idx}
                    onClick={() => setIdx(i)}
                    aria-label={`Aller à l'étape ${i+1}`}
                  />
                ))}
              </Dots>
              <Counter>{t("step_of", {n: idx+1, total: TOTAL})}</Counter>
            </Progress>

            <Carousel onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
              <Track $idx={idx} $isRtl={isRtl}>

                {/* ─── STEP 1 ─── */}
                <Step>
                  <StepHeader>
                    <StepNumber $c="green">1</StepNumber>
                    <StepTitle>{t("s1.title")}</StepTitle>
                  </StepHeader>
                  <StepBody>{renderBoldText(t("s1.body"))}</StepBody>
                  <Visual><StepOneVisual /></Visual>
                  <Hint>🎁 {t("s1.hint")}</Hint>
                </Step>

                {/* ─── STEP 2 ─── */}
                <Step>
                  <StepHeader>
                    <StepNumber $c="accent">2</StepNumber>
                    <StepTitle>{t("s2.title")}</StepTitle>
                  </StepHeader>
                  <StepBody>{renderBoldText(t("s2.body"), "#4fc3f7")}</StepBody>
                  <Visual><StepTwoVisual t={t} /></Visual>
                </Step>

                {/* ─── STEP 3 ─── */}
                <Step>
                  <StepHeader>
                    <StepNumber $c="green">3</StepNumber>
                    <StepTitle>{t("s3.title")}</StepTitle>
                  </StepHeader>
                  <StepBody>{renderBoldText(t("s3.body"))}</StepBody>
                  <Visual><StepThreeVisual t={t} /></Visual>
                  <Hint>✓ {t("s3.hint")}</Hint>
                </Step>

                {/* ─── STEP 4 ─── */}
                <Step>
                  <StepHeader>
                    <StepNumber $c="gold">4</StepNumber>
                    <StepTitle>{t("s4.title")}</StepTitle>
                  </StepHeader>
                  <StepBody>{renderBoldText(t("s4.body"), "#ffc107")}</StepBody>
                  <Visual><StepFourVisual /></Visual>
                </Step>

                {/* ─── STEP 5 ─── */}
                <Step>
                  <StepHeader>
                    <StepNumber $c="green">5</StepNumber>
                    <StepTitle>{t("s5.title")}</StepTitle>
                  </StepHeader>
                  <StepBody>{renderBoldText(t("s5.body"))}</StepBody>
                  <Visual><StepFiveVisual t={t} /></Visual>
                  <TipBlock>{t("s5.tip")}</TipBlock>
                </Step>

                {/* ─── STEP 6 — CTA ─── */}
                <Step>
                  <CtaWrap>
                    <CtaIcon>{user ? "🚀" : "🎁"}</CtaIcon>
                    <CtaTitle>{user ? t("s6.title_in") : t("s6.title")}</CtaTitle>
                    <CtaBody>
                      {renderBoldText(user ? t("s6.body_in") : t("s6.body"))}
                    </CtaBody>
                    <CtaButtons>
                      {user ? (
                        <PrimaryBtn onClick={handleGo}>{t("btn.go")} →</PrimaryBtn>
                      ) : (
                        <>
                          <PrimaryBtn onClick={handleSignup}>{t("btn.signup")}</PrimaryBtn>
                          <SecondaryBtn onClick={handleLogin}>{t("btn.login")}</SecondaryBtn>
                        </>
                      )}
                    </CtaButtons>
                  </CtaWrap>
                </Step>

              </Track>
            </Carousel>

            <Nav>
              <NavBtn onClick={prev} disabled={idx === 0}>
                ← {t("btn.prev")}
              </NavBtn>
              {isLast ? (
                <NavBtn $primary onClick={handleClose}>
                  ✓ {user ? t("btn.go") : t("btn.start")}
                </NavBtn>
              ) : (
                <NavBtn $primary onClick={next}>
                  {t("btn.next")} →
                </NavBtn>
              )}
            </Nav>
          </Modal>
        </Overlay>
      )}
    </>
  );
}
